import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import RelatedCalculators from '../components/RelatedCalculators';
import { detectLocaleSync } from '../locales/config';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e" };

const faqItems = [
  {
    q: 'What is a credit card payoff calculator?',
    a: "It's a tool that shows exactly when you'll pay off your credit card based on your current balance, interest rate, and monthly payment amount.",
  },
  {
    q: 'How much extra should I pay to get out of debt faster?',
    a: 'Even $50 extra per month can cut years off your repayment timeline. Use the "Extra Payment Scenarios" above to see the exact impact.',
  },
  {
    q: 'Does this calculator work for multiple credit cards?',
    a: 'Yes — click "+ Add debt" to enter all your credit cards at once and get a combined payoff plan.',
  },
  {
    q: "What's the difference between APR and interest rate?",
    a: "For credit cards they're effectively the same. APR (Annual Percentage Rate) is the yearly cost of borrowing, used to calculate your monthly interest charge.",
  },
];

export default function CreditCardCalculator() {
  const locale = detectLocaleSync();
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <title>Free Credit Card Payoff Calculator — ClearMyDebt</title>
        <meta name="description" content="Calculate exactly when you'll pay off your credit card debt. Enter your balance, APR, and minimum payment — get your free payoff date instantly." />
        <link rel="canonical" href="https://clearmydebt.app/credit-card-payoff-calculator/" />
        <meta property="og:title" content="Free Credit Card Payoff Calculator — ClearMyDebt" />
        <meta property="og:description" content="Calculate exactly when you'll pay off your credit card debt. Free, instant, no signup." />
        <meta property="og:url" content="https://clearmydebt.app/credit-card-payoff-calculator/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 0' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: '12px' }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
        </div>
        <h1 style={{ fontSize: 'clamp(24px,5vw,36px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', background: `linear-gradient(135deg,#fff,${COLORS.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Free Credit Card Payoff Calculator
        </h1>
        <p style={{ color: COLORS.muted, fontSize: '1.05rem', marginBottom: 0, lineHeight: 1.6 }}>
          Enter your credit card balance, interest rate, and minimum payment to see exactly
          when you'll be debt-free — and how much interest you'll save by paying extra.
        </p>
      </div>

      <Calculator
        defaultDebtType="Credit Card"
        lockedDebtType={false}
        locale={locale}
        showHelmet={false}
        showHeader={false}
      />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 64px' }}>
        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How to use this calculator
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Enter your current balance, annual interest rate (APR), and the minimum payment
          you make each month. Then use the "Extra Payment" scenarios to see how much faster
          you can pay off your debt — and how many dollars you'll save in interest.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How credit card interest works
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Credit cards use compound interest, calculated daily on your outstanding balance.
          The average US credit card APR in 2025 is around 24%. On a $5,000 balance,
          paying only the minimum can take over 15 years and cost $4,000+ in interest alone.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 20px' }}>
          Frequently Asked Questions
        </h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: '#13131a', borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

      </div>

      <RelatedCalculators locale={locale} exclude="/credit-card-payoff-calculator/" />
    </div>
  );
}
